import {Injectable} from '@angular/core';
import {Environment} from '../../shared/environment/environment';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {TvshopConstants} from '../../shared/tvshop-constants';
import {filter, map, mergeMap} from 'rxjs/operators';
import {EligibleDtvProducts} from '../../model/ocapi/eligible-dtv-products';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {Cached} from '@ngx-cache/core';
import {CacheConstants} from '../../shared/cache-constants';
import {ApiGatewayService} from './api-gateway.service';
import {SilentAuthService} from './silent-auth.service';
import {TrialSalesOffer} from '../../model/ocapi/trial-sales-offer';
import {OcapiUrlConstants} from '../../shared/ocapi-url-constants';
import {UserTypeDataService} from '../user-type/user-type.data.service';
import {OmapiIdsUtils} from '../../shared/utils/omapi-ids/omapi-ids-utils';

const INITIAL_VALUE = 'initialValue';

@Injectable()
export class OcapiService {
  public dtvIdentifier: string;
  private accessToken: string;
  private customerNumber: string;
  private cpeidBehaviorSbj = new BehaviorSubject(INITIAL_VALUE);

  constructor(private httpClient: HttpClient,
              private apiGatewayService: ApiGatewayService,
              private silentAuthService: SilentAuthService,
              private userTypeDataService: UserTypeDataService) {
  }

  @Cached(CacheConstants.DTV_CATALOG)
  public getDtvCatalog(): Observable<EligibleDtvProducts> {
    const eligibledtvproductsUrl = this.getOcapiAdaptorUrl() + '/?p=eligibledtvproducts';
    return this.httpClient.get<EligibleDtvProducts>(eligibledtvproductsUrl, this.createHeaderOptions(this.accessToken));
  }

  @Cached(CacheConstants.LOGIN)
  public login(): Observable<string> {
    return this.getCpeIdBehaviorSubject().pipe(
      mergeMap(cpeidParam => {
        if (cpeidParam) {
          return this.apiGatewayService.retrieveJwtToken(cpeidParam);
        } else {
          return this.silentAuthService.retrieveJwtToken();
        }
      }),
      mergeMap(jwtToken => {
          this.userTypeDataService.setUserType(jwtToken);
          const openidUrl = this.getBaseUrl() + '/openid/oauth/token?client_id=eos_tv&client_secret=test&grant_type=urn:ietf:params:oauth:grant-type:jwt-bearer&assertion=' + jwtToken;
          return this.httpClient.get(openidUrl, {withCredentials: true})
            .pipe(
              map(oauthResponse => {
                this.accessToken = oauthResponse['access_token'];
                OmapiIdsUtils.accessToken = this.accessToken;
                return this.accessToken;
              })
            );
        }
      ),
      mergeMap(accessToken => {
          const userDetailUrl = this.getBaseUrl() + '/ocapi/oauth/userdetails';

          const httpOptions = this.createHeaderOptions(accessToken);
          return this.httpClient.get(userDetailUrl, httpOptions)
            .pipe(
              mergeMap(response => {
                this.customerNumber = response[TvshopConstants.CUSTOMER_NUMBER_KEY];
                this.dtvIdentifier = response[TvshopConstants.DTV_IDENTIFIER_KEY];
                OmapiIdsUtils.dtvIdentifier = this.dtvIdentifier;

                if (OmapiIdsUtils.isDwnUser()) {
                  return this.getCustomerData(this.customerNumber)
                    .pipe(
                      map((customerData: any) => {
                        this.userTypeDataService.setDarwinUserType(customerData.category);
                        OmapiIdsUtils.setCorrectConstantsGlobally();
                        return this.dtvIdentifier;
                    })
                  );
                } else {
                  OmapiIdsUtils.setCorrectConstantsGlobally();
                  return of(this.dtvIdentifier);
                }
              }),
              map(dtvIdentifier => {
                return dtvIdentifier;
              }),
            );
        }
      )
    );
  }

  public getCustomerProductHolding(): Observable<Object> {
    const url = this.getOcapiAdaptorUrl() + '/?p=customerproductholding';
    const httpOptions = this.createHeaderOptions(this.accessToken);

    return this.httpClient.get(url, httpOptions);
  }

  public getPromotions(): Observable<Object> {
    const url = this.getOcapiAdaptorUrl() + '/?p=promotions';
    const httpOptions = this.createHeaderOptions(this.accessToken);

    return this.httpClient.get(url, httpOptions);
  }

  public getCustomerData(customerNumber: string): Observable<Object> {
    const httpOptions = this.createHeaderOptions(this.accessToken);
    const url = this.getBaseUrl() + '/ocapi/public/api/customer-service/v1/customers?user=' + customerNumber;
    return this.httpClient.get(url, httpOptions);
  }

  private createHeaderOptions(accessToken) {
    return {
      withCredentials: true,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      })
    };
  }

  public retrieveTrialOffers(voucherCode: string): Observable<Array<TrialSalesOffer>> {
    const url = this.getOcapiAdaptorQueryUrl() + '/?t=retrievetrialsalesofferings';
    return this.httpClient.post<Array<TrialSalesOffer>>(url, this.createRetrieveTrialRequestBody(voucherCode), this.createHeaderOptions(this.accessToken))
      .pipe(map(response => this.mapResponseToTrialSalesOffers(response)));
  }

  private mapResponseToTrialSalesOffers(offers: Array<TrialSalesOffer>) {
    const mappedOffers = [];
    offers.forEach(trialOffer => mappedOffers.push(trialOffer));
    return mappedOffers;
  }

  private getBaseUrl(): string {
    return Environment.environmentConfig().baseApiUrl;
  }

  private getOcapiAdaptorQueryUrl(): string {
    return this.getBaseUrl() + OcapiUrlConstants.OCAPI_ADAPTOR_QUERY;
  }

  private getOcapiAdaptorUrl(): string {
    return this.getBaseUrl() + OcapiUrlConstants.OCAPI_ADAPTOR;
  }

  public getAccessToken(): string {
    return this.accessToken;
  }

  public getDtvIdentifier(): Observable<string> {
    return of(this.dtvIdentifier);
  }

  public getCustomerNumber(): Observable<string> {
    return of(this.customerNumber);
  }

  public setCpeidBehaviorSbj(cpeid) {
    setTimeout(() => {
      this.cpeidBehaviorSbj.next(cpeid);
    });
  }

  private createRetrieveTrialRequestBody(voucherCode: string): object {
    return {
      dtvidentifier: this.dtvIdentifier,
      voucherid: voucherCode
    };
  }

  public getAddressFromCustomerProductHolding(customerProductHolding: any, dtvIdentifier: string): any {
    let dtvProduct = this.getDtvProduct(customerProductHolding, dtvIdentifier);
    return dtvProduct ? dtvProduct.address : {};
  }

  public getLocationIdFromCustomerProductHolding(customerProductHolding: any, dtvIdentifier: string): any {
    let dtvProduct = this.getDtvProduct(customerProductHolding, dtvIdentifier);
    return dtvProduct ? dtvProduct.locationid : null;
  }

  private getDtvProduct(customerProductHolding: any, dtvIdentifier: string) {
    let dtvProduct;
    if (customerProductHolding && customerProductHolding.customerproductholding) {
      dtvProduct = customerProductHolding.customerproductholding.filter(this.hasIdentifier(dtvIdentifier)).pop();
    }
    if (!dtvProduct) {
      dtvProduct = this.getDtvProductFromCustomerProductHoldingProducts(customerProductHolding, dtvIdentifier);
    }
    return dtvProduct;
  }

  private getDtvProductFromCustomerProductHoldingProducts(customerProductHolding: any, dtvIdentifier: string) {
    return customerProductHolding.customerproductholding.filter(this.hasProductWithDtvIdentifier(dtvIdentifier)).pop();
  }

  private hasProductWithDtvIdentifier(dtvIdentifier: string) {
    return product => product.products.filter(this.hasIdentifier(dtvIdentifier)).pop();
  }

  private hasIdentifier(dtvIdentifier: string) {
    return product => product.identifier === dtvIdentifier;
  }

  public getCpeIdBehaviorSubject(): Observable<string> {
    return this.cpeidBehaviorSbj.pipe(filter(cpeid => cpeid !== INITIAL_VALUE));
  }
}
